'use client';

import { usePathname, useRouter } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { type Board } from '@air/api/types';
import {
  formatLibrariesListResponse,
  GENERAL_LIBRARY_ID,
  Sidebar,
  useBoard,
  useLibrariesList,
  useTrackSwitchedWorkspace,
  useTrackViewedBoard,
  useTrackViewedFavorites,
  useTrackViewedLibrary,
  useTrackViewedRecent,
  useWorkspaces,
  WorkspaceSelector,
} from '@air/plugin-ui';
import { configureAPIPackage, useLogoutMutation } from '@air/utils-auth';
import { getLastUsedWorkspace } from '@air/utils-workspace';

import { PREMIERE_APP_ROUTES } from '~/constants/premiereAppRoutes';
import { ROUTES } from '~/constants/routes';
import { useURLBoardId } from '~/hooks/url/useURLBoardId';
import { useURLLibraryId } from '~/hooks/url/useURLLibraryId';
import { useSelectedWorkspaceDetails } from '~/hooks/useSelectedWorkspaceDetails';
import { SIDEBAR_WIDTH_CSS_VAR } from '~/stores/useUiPreferencesStore';
import { uxpUrlOpener, windowUrlOpener } from '~/utils/windowUrlOpener';

export const WorkspaceSidebar = memo(() => {
  const router = useRouter();
  const pathname = usePathname();
  const { logoutMutation } = useLogoutMutation();
  const { data: workspaces, isLoading: isWorkspacesLoading } = useWorkspaces();
  const { selectedWorkspace, selectedWorkspaceId } = useSelectedWorkspaceDetails();
  const { trackSwitchedWorkspace } = useTrackSwitchedWorkspace();
  const { trackViewedBoard } = useTrackViewedBoard();
  const { trackViewedFavorites } = useTrackViewedFavorites();
  const { trackViewedLibrary } = useTrackViewedLibrary();
  const { trackViewedRecent } = useTrackViewedRecent();

  const selectedBoardId = useURLBoardId();
  const selectedLibraryId = useURLLibraryId();

  // Workspace lists can change underfoot (user removed from workspace,
  // workspace deleted) — fall back to a workspace the user still belongs to
  // instead of leaving them stranded on a dead URL.
  useEffect(() => {
    if (!workspaces?.length) {
      return;
    }
    if (!selectedWorkspaceId) {
      const wsId = getLastUsedWorkspace(workspaces).id;
      router.replace(PREMIERE_APP_ROUTES.ALL(wsId));
    } else {
      const isAccessible = workspaces.some((w) => w.id === selectedWorkspaceId);

      if (!isAccessible) {
        const wsId = getLastUsedWorkspace(workspaces).id;
        router.replace(PREMIERE_APP_ROUTES.ALL(wsId));
      }
    }
  }, [workspaces, selectedWorkspaceId, router]);

  // `isMemberOf: 'YES'` matches the core app — without it, the backend
  // returns every library in the workspace including ones the user
  // doesn't have access to, leaking their existence in the sidebar.
  const { data: librariesData } = useLibrariesList({ isMemberOf: 'YES', workspaceId: selectedWorkspaceId });
  const libraries = useMemo(() => formatLibrariesListResponse(librariesData).libraries ?? [], [librariesData]);

  const { data: selectedBoard, isError: isBoardError } = useBoard({
    boardId: selectedBoardId,
    includeAncestors: true,
    workspaceId: selectedWorkspaceId,
  });

  // Board gone — fall back to all view
  useEffect(() => {
    if (selectedBoardId && isBoardError && selectedWorkspaceId) {
      router.replace(PREMIERE_APP_ROUTES.ALL(selectedWorkspaceId));
    }
  }, [selectedBoardId, isBoardError, selectedWorkspaceId, router]);

  // Library gone — fall back to all view
  useEffect(() => {
    if (!librariesData || !selectedLibraryId || selectedLibraryId === GENERAL_LIBRARY_ID) {
      return;
    }
    if (!libraries.some((lib) => lib.id === selectedLibraryId) && selectedWorkspaceId) {
      router.replace(PREMIERE_APP_ROUTES.ALL(selectedWorkspaceId));
    }
  }, [librariesData, libraries, selectedLibraryId, selectedWorkspaceId, router]);

  const handleWorkspaceChange = useCallback(
    (workspaceId: string) => {
      trackSwitchedWorkspace({ workspace_id: workspaceId });
      router.push(PREMIERE_APP_ROUTES.ALL(workspaceId));
    },
    [router, trackSwitchedWorkspace],
  );

  const handleLibraryClick = useCallback(
    (libraryId: string) => {
      if (!selectedWorkspaceId) {
        return;
      }
      trackViewedLibrary({ library_id: libraryId });
      router.push(PREMIERE_APP_ROUTES.LIBRARY(selectedWorkspaceId, libraryId));
    },
    [router, selectedWorkspaceId, trackViewedLibrary],
  );

  const handleBoardClick = useCallback(
    (board: Board) => {
      if (!selectedWorkspaceId) {
        return;
      }
      trackViewedBoard({ board_id: board.id });
      router.push(PREMIERE_APP_ROUTES.BOARD(selectedWorkspaceId, board.id));
    },
    [router, selectedWorkspaceId, trackViewedBoard],
  );

  const handleSearchClick = useCallback(() => {
    if (!selectedWorkspaceId) {
      return;
    }
    router.push(PREMIERE_APP_ROUTES.ALL(selectedWorkspaceId));
  }, [router, selectedWorkspaceId]);

  const handleFavoritesClick = useCallback(() => {
    if (!selectedWorkspaceId) {
      return;
    }
    trackViewedFavorites();
    router.push(PREMIERE_APP_ROUTES.FAVORITES(selectedWorkspaceId));
  }, [router, selectedWorkspaceId, trackViewedFavorites]);

  const handleRecentClick = useCallback(() => {
    if (!selectedWorkspaceId) {
      return;
    }
    trackViewedRecent();
    router.push(PREMIERE_APP_ROUTES.RECENT(selectedWorkspaceId));
  }, [router, selectedWorkspaceId, trackViewedRecent]);

  const handleLogout = useCallback(() => {
    // Logout's Cognito signOut redirect needs to land back inside the
    // WebView so cookies clear here — uxpUrlOpener would send it to the
    // system browser. Restore uxpUrlOpener once the mutation settles so
    // the next sign-in (which routes external IdPs through the system
    // browser) doesn't try to navigate the WebView to Google/Apple.
    configureAPIPackage({ oauthConfigParams: { urlOpener: windowUrlOpener } });
    logoutMutation.mutate(
      {},
      {
        onSettled: () => {
          configureAPIPackage({ oauthConfigParams: { urlOpener: uxpUrlOpener } });
          router.push(ROUTES.LOGIN);
        },
      },
    );
  }, [logoutMutation, router]);

  const isFavoritesView = pathname?.includes('/favorites') ?? false;
  const isRecentView = pathname?.includes('/recent') ?? false;
  const isSearchActive = !selectedLibraryId && !selectedBoardId && !isFavoritesView && !isRecentView;

  const selectedBoardAncestorIds = useMemo(
    () => selectedBoard?.ancestors?.map((a) => a.id),
    [selectedBoard?.ancestors],
  );

  const workspaceSelectorElement = useMemo(
    () => (
      <WorkspaceSelector
        isLoading={isWorkspacesLoading}
        onLogout={handleLogout}
        onWorkspaceChange={handleWorkspaceChange}
        selectedWorkspace={selectedWorkspace}
        workspaces={workspaces ?? []}
      />
    ),
    [workspaces, selectedWorkspace, handleWorkspaceChange, handleLogout, isWorkspacesLoading],
  );

  if (!selectedWorkspaceId) {
    return null;
  }

  return (
    <Sidebar
      isFavoritesActive={isFavoritesView}
      isRecentActive={isRecentView}
      isSearchActive={isSearchActive}
      libraries={libraries}
      onBoardClick={handleBoardClick}
      onFavoritesClick={handleFavoritesClick}
      onLibraryClick={handleLibraryClick}
      onRecentClick={handleRecentClick}
      onSearchClick={handleSearchClick}
      selectedBoardAncestorIds={selectedBoardAncestorIds}
      selectedBoardId={selectedBoardId}
      selectedLibraryId={selectedLibraryId}
      showGeneralLibrary
      widthCssVar={SIDEBAR_WIDTH_CSS_VAR}
      workspaceId={selectedWorkspaceId}
      workspaceSelector={workspaceSelectorElement}
    />
  );
});

WorkspaceSidebar.displayName = 'WorkspaceSidebar';
